import React, { useState, useEffect } from 'react';
import { X, Save, User, Briefcase, Mail, Phone, MapPin, Linkedin, Globe, FileText } from 'lucide-react';

export const AddLeadModal = ({ isOpen, onClose, onSave, editingLead = null, isDarkMode = true }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    company: '',
    email: '',
    phone: '',
    linkedin: '',
    website: '',
    location: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingLead) {
      setFormData({
        name: editingLead.name || '',
        role: editingLead.role || '',
        company: editingLead.company || '',
        email: editingLead.email || '',
        phone: editingLead.phone || '',
        linkedin: editingLead.linkedin || '',
        website: editingLead.website || '',
        location: editingLead.location || '',
        description: editingLead.description || ''
      });
    } else {
      setFormData({
        name: '',
        role: '',
        company: '',
        email: '',
        phone: '',
        linkedin: '',
        website: '',
        location: '',
        description: ''
      });
    }
    setErrors({});
  }, [editingLead, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.linkedin && !formData.linkedin.includes('linkedin.com')) {
      newErrors.linkedin = 'Must be a valid LinkedIn URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const leadData = {
        ...formData,
        id: editingLead?.id || `lead_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'lead',
        saved: true,
        manuallyAdded: true,
        createdAt: editingLead?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onSave(leadData);
      onClose();
    } catch (error) {
      console.error('Error saving lead:', error);
      setErrors({ submit: 'Failed to save lead. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-white';
  const textClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const borderClass = isDarkMode ? 'border-slate-700' : 'border-gray-200';
  const inputBgClass = isDarkMode ? 'bg-slate-800' : 'bg-white';
  const inputBorderClass = isDarkMode ? 'border-slate-700' : 'border-gray-300';
  const labelClass = isDarkMode ? 'text-slate-300' : 'text-gray-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${bgClass} w-full max-w-xl rounded-xl shadow-2xl flex flex-col max-h-[90vh]`}>
        {/* Header */}
        <div className={`p-6 border-b ${borderClass} flex items-center justify-between`}>
          <div>
            <h2 className={`text-2xl font-bold ${textClass}`}>
              {editingLead ? 'Edit Lead' : 'New Lead'}
            </h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Enter lead details to add to your saved list
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Full Name *
              </label>
              <div className="relative">
                <User className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  } ${errors.name ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Role / Title
              </label>
              <div className="relative">
                <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  placeholder="CEO, Marketing Director, etc."
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  }`}
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Company *
              </label>
              <div className="relative">
                <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  placeholder="Company Name"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  } ${errors.company ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.company && (
                <p className="text-red-500 text-xs mt-1">{errors.company}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Email
              </label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@company.com"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  } ${errors.email ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Phone
              </label>
              <div className="relative">
                <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  }`}
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                LinkedIn Profile
              </label>
              <div className="relative">
                <Linkedin className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/johndoe"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  } ${errors.linkedin ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.linkedin && (
                <p className="text-red-500 text-xs mt-1">{errors.linkedin}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Website
              </label>
              <div className="relative">
                <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://company.com"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  }`}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Location
              </label>
              <div className="relative">
                <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="New York, NY"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  }`}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${labelClass}`}>
                Notes / Description
              </label>
              <div className="relative">
                <FileText className={`absolute left-3 top-3 h-5 w-5 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Additional notes about this lead..."
                  rows={3}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none ${
                    isDarkMode
                      ? `${inputBgClass} ${inputBorderClass} text-white placeholder-slate-500`
                      : `${inputBgClass} ${inputBorderClass} text-gray-900 placeholder-gray-400`
                  }`}
                />
              </div>
            </div>

            {errors.submit && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {errors.submit}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className={`p-6 border-t ${borderClass} flex justify-end gap-3`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              isDarkMode
                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {saving ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {editingLead ? 'Update Lead' : 'Add Lead'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};